import React, { useEffect, useRef, useState } from 'react';
import Cardumen from './cardumen';
import Quiz from './Quiz';
import translations from './translations';
import { copy, LINKS } from './copy';
import { startTracking, track, trackAmazon, trackView, watchSections, subscribe, SUBSTACK_URL } from './tracking';

const langFromUrl = () => {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/es') return 'es';
  const q = new URLSearchParams(window.location.search).get('lang');
  return q === 'es' ? 'es' : 'en';
};

// ---------- piezas chicas ----------
const EyeGlyph = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
    <circle cx="14.6" cy="11" r="3" fill="currentColor" />
  </svg>
);

const Stars = ({ className = '' }) => (
  <span className={`tracking-[0.15em] text-coral ${className}`} aria-label="5 stars">★★★★★</span>
);

// ojos que te siguen con la mirada (los mismos PNG del cardumen), uno por sección
const watchers = new Set();
let pointer = null, watchRaf = 0;
function watchLoop(t) {
  watchRaf = 0;
  watchers.forEach((el) => {
    const b = el.getBoundingClientRect();
    if (b.bottom < -50 || b.top > window.innerHeight + 50) return;
    const cx = b.left + b.width / 2, cy = b.top + b.height / 2;
    // sin mouse (celular): miran hacia el centro de la pantalla y se mueven un poco solos
    const tx = pointer ? pointer[0] : window.innerWidth / 2 + Math.sin(t / 1400 + cx) * 160;
    const ty = pointer ? pointer[1] : window.innerHeight / 2 + Math.cos(t / 1700 + cy) * 120;
    const a = Math.atan2(ty - cy, tx - cx);
    const left = Math.cos(a) < 0;
    el.style.transform = `rotate(${a}rad) scaleY(${left ? -1 : 1})`;
  });
  if (watchers.size) watchRaf = requestAnimationFrame(watchLoop);
}
if (typeof window !== 'undefined') {
  window.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') pointer = [e.clientX, e.clientY]; }, { passive: true });
}
function WatchingEye({ n = 1, red = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    watchers.add(el);
    if (!watchRaf) watchRaf = requestAnimationFrame(watchLoop);
    return () => watchers.delete(el);
  }, []);
  return (
    <span aria-hidden="true" className={`blink inline-block ${className}`} style={{ animationDelay: `${(n * 1.7) % 6}s` }}>
      <img ref={ref} src={red ? `/eye${n}-red.png` : `/eye${n}.png`} alt="" className="block h-auto w-full transition-transform duration-150 ease-out" />
    </span>
  );
}

// título de sección: un ojo que mira + el resaltado coral de la tapa en la parte clave
const Title = ({ parts, eye = 1, red = false, light = false, className = '', size = 'text-[clamp(2.1rem,4.6vw,3.4rem)]' }) => (
  <div className={`mb-7 ${className}`}>
    <WatchingEye n={eye} red={red} className="mb-4 w-[3.2rem]" />
    <h2 className={`${size} font-semibold leading-[1.08] tracking-tight ${light ? 'text-paper-light' : ''}`}>
      {parts[0]}<mark className={light ? 'hl-dark' : 'hl'}>{parts[1]}</mark>{parts[2]}
    </h2>
  </div>
);

const Tag = ({ children, light }) => (
  <p className={`mb-5 inline-block rounded-full border px-3.5 py-1 font-sans text-sm ${light ? 'border-paper/30 text-paper/80' : 'border-ink/20 text-ink-soft'}`}>{children}</p>
);

const AmazonLink = ({ href, edition, format, store = 'amazon-us', location = '', className, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className}
     onClick={() => trackAmazon(edition, format, store, location)}>
    {children}
  </a>
);

// doodles del libro (fondo blanco): multiply los funde con el papel
const Doodle = ({ src, alt = '', className = '' }) => (
  <img src={src} alt={alt} loading="lazy" className={`mix-blend-multiply ${className}`} />
);

const Section = ({ id, section, className = '', children, inner = '' }) => (
  <section id={id} data-section={section} className={`relative px-5 sm:px-8 ${className}`}>
    <div className={`mx-auto max-w-page ${inner}`}>{children}</div>
  </section>
);

function MailForm({ t, origin, button, small, dark }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | ok | error
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setState('error'); return; }
    setState('sending');
    try { await subscribe(email.trim(), origin); setState('ok'); } catch { setState('ok'); }
  };
  if (state === 'ok') {
    return (
      <div className={`rounded-2xl border px-5 py-4 font-sans ${dark ? 'border-paper/30 text-paper-light' : 'border-ink/15 bg-paper-light'}`} role="status">
        <p className="font-semibold">{t.ok}</p>
        <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" onClick={() => track('substack_fallback_click', { origin })} className="mt-1 inline-block text-sm underline underline-offset-4 opacity-70 hover:opacity-100">{t.fallback}</a>
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} noValidate className="font-sans">
      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="sr-only" htmlFor={`mail-${origin}`}>Email</label>
        <input id={`mail-${origin}`} type="email" inputMode="email" autoComplete="email" required value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
          placeholder={t.placeholder}
          className={`min-w-0 flex-1 rounded-full border px-5 py-3 text-base outline-none transition focus:border-coral ${dark ? 'border-paper/40 bg-transparent text-paper-light placeholder:text-paper/50' : 'border-ink/25 bg-paper-light text-ink placeholder:text-ink-mute'}`} />
        <button type="submit" disabled={state === 'sending'} className={`${dark ? 'btn-coral' : 'btn-ink'} shrink-0 disabled:opacity-60`}>
          {button || t.button}
        </button>
      </div>
      {state === 'error' && <p className="mt-2 text-sm text-coral-deep" role="alert">{t.error}</p>}
      <p className={`mt-3 text-sm ${dark ? 'text-paper/60' : 'text-ink-mute'}`}>{small || t.small}</p>
    </form>
  );
}

// ---------- página ----------
export default function Landing() {
  const [lang] = useState(langFromUrl);
  const c = copy[lang];
  const tq = translations[lang];
  const [showQuiz, setShowQuiz] = useState(false);
  const [openStores, setOpenStores] = useState(false);
  const heroApi = useRef(null);
  const heroRef = useRef(null);
  const [wordsOn, setWordsOn] = useState(0);

  useEffect(() => {
    startTracking(lang);
    document.documentElement.lang = lang;
    document.title = c.meta.title;
    const set = (sel, attr, val) => { const el = document.querySelector(sel); if (el) el.setAttribute(attr, val); };
    set('meta[name="description"]', 'content', c.meta.description);
    set('link[rel="canonical"]', 'href', lang === 'es' ? 'https://www.thebehavioralstartup.com/es' : 'https://www.thebehavioralstartup.com/');
    if (lang === 'es') trackView('es');
    if (new URLSearchParams(window.location.search).get('quiz') === '1') { track('quiz_open', { location: 'url' }); setShowQuiz(true); }
    return watchSections();
  }, [lang, c]);

  // clics en botones internos marcados con data-cta="nombre" y en redes (Amazon y quiz se miden aparte)
  useEffect(() => {
    const on = (e) => {
      const el = e.target.closest('[data-cta]');
      if (el) track('cta_click', { cta: el.dataset.cta });
      const out = e.target.closest('a[data-social]');
      if (out) track('social_click', { network: out.dataset.social });
    };
    document.addEventListener('click', on);
    return () => document.removeEventListener('click', on);
  }, []);

  // el título entra palabra por palabra y cada palabra espanta al cardumen, como los subtítulos de los reels
  useEffect(() => {
    const times = [250, 650, 1050];
    const ids = times.map((ms, k) => setTimeout(() => {
      setWordsOn(k + 1);
      const w = heroRef.current && heroRef.current.querySelector(`[data-word="${k}"]`);
      if (w && heroApi.current) {
        const hb = heroRef.current.getBoundingClientRect(), b = w.getBoundingClientRect();
        heroApi.current.startle(b.left + b.width / 2 - hb.left, b.top + b.height / 2 - hb.top);
      }
    }, ms));
    return () => ids.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showQuiz ? 'hidden' : '';
    const onKey = (e) => { if (e.key === 'Escape') setShowQuiz(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showQuiz]);

  const openQuiz = (location) => { track('quiz_open', { location }); setShowQuiz(true); };
  const onEyes = (where) => track('eyes_interact', { where });
  const esKindle = { href: LINKS.kindleEs, edition: 'es', format: 'kindle' };

  return (
    <div className="min-h-screen overflow-x-clip">
      {/* ---------- nav ---------- */}
      <header className="fixed inset-x-0 top-0 z-40 border-b border-ink/10 bg-white/85 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <nav className="mx-auto flex h-16 max-w-page items-center justify-between gap-4 px-5 sm:px-8" aria-label="Main">
          <a href="#top" className="flex items-baseline gap-1.5 whitespace-nowrap text-ink">
            <span className="text-sm italic text-ink-mute">{lang === 'es' ? 'La' : 'the'}</span>
            <span className="text-lg font-semibold tracking-tight sm:text-xl">Behavioral Startup</span>
          </a>
          <div className="flex items-center gap-1 font-sans text-[0.92rem] sm:gap-2">
            <a href="#method" data-cta="nav_method" className="hidden rounded-full px-3 py-2 hover:bg-ink/5 md:inline-block">{c.nav.method}</a>
            <a href="#reviews" data-cta="nav_reviews" className="hidden rounded-full px-3 py-2 hover:bg-ink/5 md:inline-block">{c.nav.reviews}</a>
            <button onClick={() => openQuiz('nav')} className="hidden rounded-full px-3 py-2 hover:bg-ink/5 md:inline-block">{c.nav.quiz}</button>
            <a href="#author" data-cta="nav_author" className="hidden rounded-full px-3 py-2 hover:bg-ink/5 lg:inline-block">{c.nav.author}</a>
            <a href={lang === 'es' ? '#es' : '#buy'} data-cta="nav_buy" className="btn-ink !px-4 !py-2 text-sm">{c.nav.buy}</a>
            <a href={c.nav.otherHref} hrefLang={lang === 'es' ? 'en' : 'es'} title={c.nav.otherLabel} data-cta={lang === 'es' ? 'lang_to_en' : 'lang_to_es'}
               className="rounded-full border border-ink/20 px-3 py-2 text-xs font-semibold tracking-wider text-ink-soft hover:border-ink">{c.nav.other}</a>
          </div>
        </nav>
      </header>

      {/* ---------- hero con cardumen ---------- */}
      <section id="top" data-section="hero" ref={heroRef} className="relative flex bg-white min-h-[100svh] items-center justify-center overflow-hidden px-5 pb-16 pt-24">
        <Cardumen apiRef={heroApi} onInteract={() => onEyes('hero')} targetSelector="[data-home]" topPad={64} density={0.6} spriteRatio={0.032} spriteMin={30} spriteMax={48} />
        <div data-avoid data-home className="relative z-10 mx-auto max-w-[44rem] text-center">
          <p className="mb-5 font-sans text-[0.95rem] text-ink-mute">{c.hero.kicker}</p>
          {lang === 'es' && (
            <p className="mb-6 inline-flex items-center gap-2 rounded-full bg-ink px-4 py-1.5 font-sans text-sm font-semibold text-paper-light">
              <span className="h-2 w-2 rounded-full bg-coral" />{c.hero.date}
            </p>
          )}
          <h1 className="leading-[0.92] tracking-tight">
            <span data-word="0" className={`block text-[clamp(1.5rem,min(4.2vw,5svh),2.8rem)] font-medium italic text-ink-soft ${wordsOn > 0 ? 'animate-rise' : 'opacity-0'}`}>{c.hero.the}</span>
            <span data-word="1" className={`block text-[clamp(3rem,min(11vw,12.5svh),7.6rem)] font-bold ${wordsOn > 1 ? 'animate-rise' : 'opacity-0'}`}>{c.hero.t1}</span>
            <span data-word="2" className={`block text-[clamp(3rem,min(11vw,12.5svh),7.6rem)] font-bold ${wordsOn > 2 ? 'animate-rise' : 'opacity-0'}`}>{c.hero.t2}</span>
          </h1>
          <EyeGlyph className="mx-auto my-6 h-9 w-9 text-ink" />
          <p className="mx-auto max-w-[30rem] text-[clamp(1.1rem,2.2vw,1.35rem)] leading-snug text-ink-soft">
            {c.hero.sub[0]}<mark className="hl text-ink">{c.hero.sub[1]}</mark>
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {lang === 'es'
              ? <AmazonLink {...esKindle} location="hero" className="btn-ink">{c.hero.cta1} <span aria-hidden>→</span></AmazonLink>
              : <a href={c.hero.cta1Href} data-cta="hero_get_book" className="btn-ink">{c.hero.cta1} <span aria-hidden>→</span></a>}
            <button onClick={() => openQuiz('hero')} className="btn-line">{c.hero.cta2}</button>
          </div>
          <p className="mt-6 font-sans text-sm text-ink-mute"><Stars className="mr-2" />{c.hero.rating}</p>
        </div>
        <p data-avoid className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap font-sans text-xs text-ink-mute">
          <span className="hidden [@media(hover:hover)]:inline">{c.hero.hint}</span>
          <span className="[@media(hover:hover)]:hidden">{c.hero.hintTouch}</span>
        </p>
      </section>

      {/* ---------- banda "también en español" (solo EN) ---------- */}
      {lang === 'en' && (
        <a href="/es" hrefLang="es" data-cta="band_es" className="group block bg-ink px-5 py-4 text-center font-sans text-sm text-paper-light sm:text-base">
          <span className="font-serif italic">{c.band.text}</span>{' '}
          <span className="text-paper/70">{c.band.date}</span>{' '}
          <span className="ml-1 font-semibold text-coral-soft underline-offset-4 group-hover:underline">{c.band.cta} →</span>
        </a>
      )}

      {/* ---------- lanzamiento ES (solo /es) ---------- */}
      {lang === 'es' && <LaunchEs c={c} onEyes={() => onEyes('cover_es')} />}

      {/* ---------- el problema + regla cero ---------- */}
      <Section id="method" section="method" className="py-24 sm:py-32" inner="grid gap-14 lg:grid-cols-[1.25fr_1fr] lg:gap-20">
        <div>
          <Title parts={c.method.title} eye={2} />
          <div className="prose-body max-w-[38rem]">{c.method.p.map((p, i) => <p key={i}>{p}</p>)}</div>
          <Doodle src={`/doodle-first-${lang}.webp`} alt={lang === 'es' ? 'Funcionalidad primero contra comportamiento primero' : 'Feature-first versus behavior-first'} className="mt-6 w-full max-w-[34rem]" />
        </div>
        <aside className="self-center">
          <div className="card relative overflow-hidden !rounded-[2rem] p-8 sm:p-10">
            <p className="font-sans text-sm font-semibold text-coral-deep">{c.method.ruleLabel}</p>
            <Doodle src={`/doodle-balance-${lang}.webp`} alt={c.method.ruleTitle} className="mx-auto -mt-2 mb-2 w-full max-w-[20rem]" />
            <p className="whitespace-nowrap text-[clamp(1.9rem,3.4vw,2.7rem)] font-bold leading-none">{c.method.ruleTitle}</p>
            <p className="mt-5 text-[1.05rem] leading-relaxed text-ink-soft">{c.method.ruleBody}</p>
          </div>
        </aside>
      </Section>

      {/* ---------- modelos ANVERSO ---------- */}
      <Section id="anverso" section="models" className="border-y border-ink/10 bg-paper-deep/60 py-24 sm:py-28">
        <div className="mb-14 flex items-end justify-between gap-10">
          <div className="max-w-[42rem]">
            <Title parts={c.models.title} eye={5} className="!mb-5" />
            <p className="text-[1.1rem] leading-relaxed text-ink-soft">{c.models.intro}</p>
          </div>
          <Doodle src={`/doodle-toolbox-${lang}.webp`} alt="" className="-mb-6 hidden w-60 shrink-0 md:block" />
        </div>
        <ol className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <span aria-hidden className="absolute left-8 right-8 top-[3.1rem] hidden h-px bg-ink/25 lg:block -z-0" />
          {c.models.items.map((m, i) => (
            <li key={m.k} className="card relative flex flex-col p-7">
              <div className="mb-5 flex items-center justify-between">
                <span className="relative z-10 flex h-[3.4rem] min-w-[3.4rem] items-center justify-center rounded-full bg-ink px-3 text-xl font-bold text-paper-light">{m.k}</span>
                <span className="font-sans text-xs font-semibold text-ink-mute">0{i + 1}</span>
              </div>
              <h3 className="mb-2 text-2xl font-semibold">{m.name}</h3>
              <p className="mb-5 leading-relaxed text-ink-soft">{m.desc}</p>
              <ul className="mt-auto space-y-2 border-t border-ink/10 pt-4 font-sans text-[0.9rem] leading-snug text-ink-soft">
                {m.points.map((p) => <li key={p} className="flex gap-2"><span className="mt-[0.45em] h-1.5 w-1.5 shrink-0 rounded-full bg-coral" />{p}</li>)}
              </ul>
            </li>
          ))}
        </ol>
        <a href={c.models.linkHref} data-cta="models_full_framework" className="mt-10 inline-flex items-center gap-2 font-sans font-semibold underline decoration-coral decoration-2 underline-offset-[6px] hover:text-coral-deep">
          {c.models.link} →
        </a>
      </Section>

      {/* ---------- sprint ---------- */}
      <Section section="sprint" className="bg-white py-24 sm:py-28" inner="grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20">
        <div>
          <Title parts={c.sprint.title} eye={8} size="text-[clamp(1.9rem,3.8vw,2.8rem)]" className="!mb-5" />
          <p className="text-[1.08rem] leading-relaxed text-ink-soft">{c.sprint.body}</p>
          <Doodle src="/doodle-team.webp" alt="" className="mt-8 w-full max-w-[17rem]" />
        </div>
        <div className="grid content-center gap-8 sm:grid-cols-3">
          {c.sprint.items.map((s, i) => (
            <div key={s.name} className="border-t-2 border-ink pt-5">
              <p className="mb-3 font-sans text-sm font-semibold text-coral-deep">0{i + 1}</p>
              <h3 className="mb-2 text-xl font-semibold">{s.name}</h3>
              <p className="leading-relaxed text-ink-soft">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- reseñas ---------- */}
      <Section id="reviews" section="reviews" className="py-24 sm:py-28">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Title parts={c.reviews.title} eye={3} className="!mb-0" />
          </div>
          <p className="font-sans text-ink-soft"><Stars className="mr-2 text-lg" />{c.reviews.rating}</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {c.reviews.items.map((r) => (
            <figure key={r.who} className="card flex flex-col p-8">
              <span aria-hidden className="-mb-4 -mt-2 text-6xl leading-none text-coral">“</span>
              <blockquote className="mb-6 text-[1.12rem] italic leading-relaxed text-ink">{r.q}</blockquote>
              <figcaption className="mt-auto font-sans text-sm"><span className="font-semibold">{r.who}</span><span className="text-ink-mute"> · {r.role}</span></figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          {c.reviews.note ? <p className="font-sans text-sm text-ink-mute">{c.reviews.note}</p> : <span />}
          <AmazonLink href={LINKS.reviews} edition="en" format="kindle" store="amazon-reviews" location="reviews" className="btn-line">{c.reviews.cta}</AmazonLink>
        </div>
      </Section>

      {/* ---------- quiz: banda oscura, el cardumen rodea el botón ---------- */}
      <QuizBand c={c} onOpen={() => openQuiz('band')} onEyes={() => onEyes('quiz')} />

      {/* ---------- comparación ---------- */}
      <Section section="compare" className="bg-white py-24 sm:py-28">
        <Title parts={c.compare.title} eye={6} className="max-w-[40rem]" />
        <p className="mb-12 max-w-[44rem] text-[1.12rem] leading-relaxed text-ink-soft">{c.compare.quote}</p>
        <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[760px] border-collapse font-sans text-sm">
            <thead>
              <tr className="border-b-2 border-ink">
                <th className="py-3 pr-4 text-left font-semibold">{c.compare.feature}</th>
                {c.compare.cols.map((col, i) => (
                  <th key={col} className={`px-3 py-3 text-center font-semibold ${i === 0 ? 'rounded-t-xl bg-ink text-paper-light' : 'text-ink-soft'}`}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.compare.rows.map(([feat, cells]) => (
                <tr key={feat} className="border-b border-ink/10">
                  <td className="py-4 pr-4 font-semibold">{feat}</td>
                  {cells.map(([ok, sub], i) => (
                    <td key={i} className={`px-3 py-4 text-center ${i === 0 ? 'bg-ink/[0.035]' : ''}`}>
                      <span className={`block text-lg ${ok ? 'text-ink' : 'text-ink/30'}`} aria-label={ok ? 'yes' : 'no'}>{ok ? '●' : '○'}</span>
                      <span className="block text-xs text-ink-mute">{sub}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ---------- comprar (EN) / edición en inglés (ES) ---------- */}
      <section id="buy" data-section="buy" className="relative overflow-hidden border-y border-ink/10 bg-paper-deep/60 px-5 py-24 sm:px-8 sm:py-28">
        <div className="mx-auto grid max-w-page items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <CoverSchool src={copy.en.cover} alt={copy.en.coverAlt} onEyes={() => onEyes('cover_en')} />
          <div className="relative z-10">
            {c.buy.tag && <Tag>{c.buy.tag}</Tag>}
            <Title parts={c.buy.title} eye={4} className="!mb-5" />
            <p className="mb-8 text-[1.08rem] leading-relaxed text-ink-soft">{c.buy.body}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {c.buy.formats.map((f) => (
                <div key={f.name} className="card flex flex-col p-6">
                  <p className="font-sans text-sm font-semibold text-ink-mute">{f.name}</p>
                  <p className="my-1 text-3xl font-bold">{f.price}</p>
                  <p className="mb-5 font-sans text-xs text-ink-mute">{f.note}</p>
                  <AmazonLink href={f.href} edition="en" format={f.format} location="buy" className="btn-ink mt-auto w-full">{f.cta}</AmazonLink>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <button onClick={() => { if (!openStores) track('stores_open'); setOpenStores((v) => !v); }} aria-expanded={openStores}
                      className="font-sans text-sm font-semibold underline decoration-ink/30 underline-offset-4 hover:decoration-coral">
                {c.buy.storesTitle} {openStores ? '−' : '+'}
              </button>
              {openStores && (
                <div className="mt-5 grid gap-6 font-sans text-sm sm:grid-cols-2">
                  {c.buy.regions.map((r) => (
                    <div key={r.name}>
                      <p className="mb-2 font-semibold">{r.name}</p>
                      <ul className="space-y-1">
                        {r.stores.map(([label, href, store]) => (
                          <li key={store}><AmazonLink href={href} edition="en" format="any" store={store} location="stores" className="text-ink-soft underline decoration-ink/20 underline-offset-4 hover:text-coral-deep">{label}</AmazonLink></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-5 font-sans text-xs leading-relaxed text-ink-mute">{c.buy.isbn}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- preguntas ---------- */}
      <Section id="faq" section="faq" className="py-24 sm:py-28" inner="grid gap-12 lg:grid-cols-[1fr_2fr] lg:gap-20">
        <div>
          <Title parts={c.faq.title} eye={7} />
          <p className="font-sans text-ink-soft">{c.faq.more}</p>
          <a href={`mailto:${LINKS.email}`} data-cta="email_faq" className="font-sans font-semibold underline decoration-coral decoration-2 underline-offset-[6px]">{c.faq.contact}</a>
          <p className="mt-1 font-sans text-sm text-ink-mute">{LINKS.email}</p>
        </div>
        <div className="divide-y divide-ink/10 border-y border-ink/10">
          {c.faq.items.map((f) => (
            <details key={f.q} className="group py-5" onToggle={(e) => { if (e.currentTarget.open) track('faq_open', { question: f.q }); }}>
              <summary className="flex cursor-pointer items-start justify-between gap-6 text-[1.15rem] font-semibold leading-snug">
                {f.q}
                <span aria-hidden className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-ink/25 font-sans text-base transition group-open:rotate-45 group-open:bg-ink group-open:text-paper-light">+</span>
              </summary>
              <p className="mt-4 max-w-[44rem] leading-[1.75] text-ink-soft">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* ---------- autor ---------- */}
      <Section id="author" section="author" className="bg-white py-24 sm:py-28" inner="grid items-center gap-12 md:grid-cols-[auto_1fr] md:gap-16">
        <div className="relative mx-auto">
          <img src="/nacho.webp" alt={c.author.photoAlt} width="288" height="288" loading="lazy"
               className="h-56 w-56 rounded-full object-cover grayscale-[20%] sm:h-72 sm:w-72" />
          <EyeGlyph className="absolute -right-1 bottom-4 h-14 w-14 rounded-full bg-paper-light p-1 text-ink" />
        </div>
        <div>
          <Title parts={c.author.title} eye={1} size="text-[clamp(1.9rem,3.8vw,2.8rem)]" />
          <div className="prose-body max-w-[40rem]">{c.author.bio.map((p, i) => <p key={i}>{p}</p>)}</div>
          <p className="mt-2 font-sans text-ink-soft">
            {c.author.work}{' '}
            <a href={c.author.workHref} data-cta="consulting_site" className="font-semibold text-ink underline decoration-coral decoration-2 underline-offset-[6px]">{c.author.workCta}</a>
          </p>
          <div className="mt-6 flex flex-wrap gap-2 font-sans text-sm">
            {[['LinkedIn', LINKS.linkedin], ['Instagram', lang === 'es' ? LINKS.instagramEs : LINKS.instagramEn], ['Medium', LINKS.medium], ['nachoparietti.com', LINKS.personal]].map(([l, h]) => (
              <a key={l} href={h} data-social={l.toLowerCase()} target="_blank" rel="noopener noreferrer" className="rounded-full border border-ink/20 px-4 py-1.5 hover:border-ink">{l}</a>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- newsletter ---------- */}
      <Section section="newsletter" className="py-24" inner="max-w-[40rem] text-center">
        <WatchingEye n={2} className="mx-auto mb-5 w-[3.6rem]" />
        <h2 className="mb-4 text-[clamp(1.8rem,3.6vw,2.5rem)] font-semibold leading-tight tracking-tight">{c.newsletter.title[0]}<mark className="hl">{c.newsletter.title[1]}</mark>{c.newsletter.title[2]}</h2>
        <p className="mb-8 text-[1.08rem] text-ink-soft">{c.newsletter.body}</p>
        <div className="text-left"><MailForm t={c.newsletter} origin="newsletter" /></div>
      </Section>

      {/* ---------- footer ---------- */}
      <footer className="bg-ink px-5 pb-10 pt-16 text-paper-light sm:px-8" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="mx-auto max-w-page">
          <div className="flex flex-wrap items-end justify-between gap-8 border-b border-paper/15 pb-10">
            <div>
              <p className="text-3xl font-semibold"><span className="text-lg italic text-paper/60">{lang === 'es' ? 'La' : 'the'} </span>Behavioral Startup</p>
              <p className="mt-2 text-paper/60">{c.footer.tagline}</p>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm">
              {c.footer.links.map(([l, h]) => (
                <li key={l}><a href={h} {...(h.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="text-paper/75 hover:text-white">{l}</a></li>
              ))}
              <li><a href={`mailto:${LINKS.email}`} className="text-paper/75 hover:text-white">{LINKS.email}</a></li>
            </ul>
          </div>
          <p className="mt-8 font-sans text-xs text-paper/50">© {new Date().getFullYear()} {c.footer.rights}</p>
        </div>
      </footer>

      {/* ---------- quiz ---------- */}
      {showQuiz && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-ink font-sans text-white" role="dialog" aria-modal="true">
          <button onClick={() => setShowQuiz(false)} aria-label="Close"
                  className="fixed right-4 top-4 z-[70] rounded-full bg-white/10 p-2 transition hover:bg-white/20">
            <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
          {/* los ojos esperan fuera del marco; al marcar una opción se juntan alrededor de ella */}
          <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0">
            <Cardumen lurk watch lookHome parkSides speed={2} scopeSelector="#quiz-root" targetSelector="[data-selected], [data-quiz-focus]"
                      density={0.14} minEyes={18} maxEyes={34} redRatio={0.25} orbitScale={0.25} avoidPad={6}
                      spriteRatio={0.03} spriteMin={26} spriteMax={40} seed={31} />
          </div>
          <div id="quiz-root" className="relative z-10"><Quiz lang={lang} t={tq} /></div>
        </div>
      )}
    </div>
  );
}

// ---------- bloques ----------
function QuizBand({ c, onOpen, onEyes }) {
  return (
    <section data-section="quiz_band" className="relative overflow-hidden bg-ink px-5 py-28 text-paper-light sm:py-36">
      <Cardumen onInteract={onEyes} targetSelector="[data-home]" density={0.32} redRatio={0.3} spriteRatio={0.026} spriteMin={26} spriteMax={40} orbitScale={0.8} seed={7} />
      <div data-avoid data-home className="relative z-10 mx-auto max-w-[36rem] text-center">
        <Tag light>{c.quiz.tag}</Tag>
        <h2 className="mb-5 text-[clamp(2rem,4.6vw,3.3rem)] font-semibold leading-tight tracking-tight">{c.quiz.title[0]}<mark className="hl-dark">{c.quiz.title[1]}</mark>{c.quiz.title[2]}</h2>
        <p className="mb-8 text-[1.1rem] leading-relaxed text-paper/75">{c.quiz.body}</p>
        <button onClick={onOpen} className="btn-coral !px-8 !py-4 text-base">{c.quiz.cta} →</button>
        <p className="mt-5 font-sans text-sm text-paper/50">{c.quiz.based}</p>
      </div>
    </section>
  );
}

// la tapa con el cardumen orbitando alrededor, como el cierre de los reels
function CoverSchool({ src, alt, className = '', onEyes }) {
  return (
    <div className={`relative mx-auto flex min-h-[26rem] w-full items-center justify-center sm:min-h-[36rem] ${className}`}>
      <Cardumen onInteract={onEyes} targetSelector="[data-home]" density={0.95} spriteRatio={0.06} spriteMin={26} spriteMax={42} orbitScale={0.55} avoidPad={14} seed={21} />
      <img data-avoid data-home src={src} alt={alt} width="400" height="600" loading="lazy"
           className="cover-shadow relative z-10 w-[min(52vw,15rem)] rotate-[-2deg] rounded-[3px] transition duration-500 hover:rotate-0 sm:w-72" />
    </div>
  );
}

function LaunchEs({ c, onEyes }) {
  const L = c.launch;
  const kindle = { href: LINKS.kindleEs, edition: 'es', format: 'kindle' };
  return (
    <section id="es" data-section="launch_es" className="relative overflow-hidden border-b border-ink/10 bg-paper-deep/60 px-5 py-24 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-page items-start gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <CoverSchool src={c.cover} alt={c.coverAlt} onEyes={onEyes} className="order-2 lg:order-1 lg:sticky lg:top-20 lg:self-start" />
        <div className="relative z-10 order-1 lg:order-2">
          <Title parts={L.title} eye={3} className="!mb-5" />
          <p className="mb-10 text-[1.12rem] leading-relaxed text-ink-soft">{L.body}</p>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="card flex flex-col p-7">
              <p className="font-sans text-sm font-semibold text-ink-mute">{L.kindle.name}</p>
              <p className="my-1 text-4xl font-bold">{L.kindle.price}</p>
              <p className="mb-4 leading-relaxed text-ink-soft">{L.kindle.text}</p>
              <p className="mb-2 font-sans text-xs font-semibold text-ink-mute">{L.kindle.pointsTitle}</p>
              <ul className="mb-4 space-y-1.5 font-sans text-sm">
                {L.kindle.points.map(([k, v]) => <li key={k} className="flex gap-2"><span className="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-coral" /><span><strong>{k}:</strong> {v}.</span></li>)}
              </ul>
              <p className="mb-6 text-[0.98rem] leading-relaxed text-ink-soft">{L.kindle.after}</p>
              <AmazonLink {...kindle} location="launch_es" className="btn-ink mt-auto w-full">{L.kindle.cta}</AmazonLink>
            </div>
            <div className="card flex flex-col p-7">
              <p className="font-sans text-sm font-semibold text-ink-mute">{L.paper.name}</p>
              <p className="my-1 text-[1.75rem] font-bold leading-tight">{L.paper.price}</p>
              <p className="mb-4 leading-relaxed text-ink-soft">{L.paper.text}</p>
              <p className="mb-2 font-sans text-xs font-semibold text-ink-mute">{L.paper.refTitle}</p>
              <ul className="mb-4 space-y-1.5 font-sans text-sm">
                {L.paper.refs.map(([k, v]) => <li key={k} className="flex gap-2"><span className="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-coral" /><span><strong>{k}:</strong> {v}.</span></li>)}
              </ul>
              <p className="mb-3 text-[0.98rem] leading-relaxed text-ink-soft">{L.paper.after}</p>
              <p className="mb-6 font-sans text-xs leading-relaxed text-ink-mute">{L.paper.deposit}</p>
              <AmazonLink href={LINKS.paperEs} edition="es" format="paperback" location="launch_es" className="btn-ink mt-auto w-full">{L.paper.cta}</AmazonLink>
            </div>
          </div>

          <p className="mt-8 font-sans text-ink-soft">
            {L.closing[0]}<a href={LINKS.instagramEs} data-social="instagram" target="_blank" rel="noopener noreferrer" className="font-semibold text-ink underline decoration-coral decoration-2 underline-offset-4">{L.closing[1]}</a>{L.closing[2]}
          </p>
        </div>
      </div>
    </section>
  );
}
